import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Deliverable } from '../../../types/deliverables';
import styles from './DeliverableTable.module.scss';
import { IconArrowNarrowDown } from '@tabler/icons-react';
import QIcon from '../../../assets/images/QIcon.svg';

// making table props cuz need filtered ones customly on other parts
interface DeliverableTableProps {
  deliverables: Deliverable[];
  itemsPerPage?: number;
  onItemsPerPageChange?: (newItemsPerPage: number) => void;
  onDeliverableClick?: (deliverable: Deliverable) => void;
  nameFilter: string;
  criticalityFilter: string[];
  phaseFilter: string[];
    labelFilter: string[];
}

type SortField = 'id' | 'name' | 'phase' | 'criticality' | 'date' | 'progress';
type SortDirection = 'asc' | 'desc';

const DeliverableTable: React.FC<DeliverableTableProps> = ({
                                                               deliverables,
  itemsPerPage = 10,
  onItemsPerPageChange,
  onDeliverableClick,
  nameFilter,
  criticalityFilter,
                                                               phaseFilter,
                                                               labelFilter
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation('deliverables');
  const [localItemsPerPage, setLocalItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('deliverableTableItemsPerPage');
    return saved ? parseInt(saved, 10) || itemsPerPage : itemsPerPage;
  });
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter and sort the deliverables
  const filteredAndSortedDeliverables = useMemo(() => {
    // First filter the deliverables
    const filtered = deliverables.filter(deliverable => {
      // Filter by name
      if (nameFilter && !deliverable.name.toLowerCase().includes(nameFilter.toLowerCase())) {
        console.log('Name filter excluded:', deliverable.name);
        return false;
      }
      
      // Filter by criticality
      if (criticalityFilter.length > 0) {
        if (deliverable.criticality) {
          // uppercase check
          const uppercaseCriticality = deliverable.criticality.toUpperCase();
          if (!criticalityFilter.includes(uppercaseCriticality)) {
            return false;
          }
        } else {
          // condition to check filtered criticality
          if (!criticalityFilter.includes('NULL')) {
            return false;
          } else {
            console.log('Including null criticality');
          }
        }
      }
      
      // Filter by phase
      if (phaseFilter.length > 0) {
        if (!phaseFilter.includes(deliverable.phase)) {
          return false;
        }
      }

        // Filter by label (OR: deliverable must have at least one selected label)
        if (labelFilter.length > 0) {
            const deliverableLabels = deliverable.labels || [];
            const hasMatch = deliverableLabels.some(l => labelFilter.includes(l.id!.toString()));
            if (!hasMatch) return false;
        }

      return true;
    });
    // sort the filtered deliverables
    return [...filtered].sort((a, b) => {
      // Default to string comparison
      let comparison = 0;
      
      switch (sortField) {
        case 'id':
          try {
            comparison = parseInt(a.id) - parseInt(b.id);
            // If use case gets NaN. fall back to string comparison
            if (isNaN(comparison)) {
              comparison = a.id.localeCompare(b.id);
            }
          } catch {
            comparison = a.id.localeCompare(b.id);
          }
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'phase':
          comparison = a.phase.localeCompare(b.phase);
          break;
        case 'criticality':
          // Convert criticality to numeric value for comparison
          const criticalityValue = (crit: string | null | undefined): number => {
            if (!crit) return 0;
            if (crit === 'HIGH' || crit === 'High') return 3;
            if (crit === 'MIDDLE' || crit === 'Medium') return 2;
            if (crit === 'EASY' || crit === 'Easy') return 1;
            return 0;
          };
          comparison = criticalityValue(a.criticality) - criticalityValue(b.criticality);
          break;
        case 'date':
          const aDate = a.rawStartDate || '';
          const bDate = b.rawStartDate || '';
          comparison = aDate.localeCompare(bDate);
          break;
        case 'progress':
          comparison = a.progress - b.progress;
          break;
      }
      
      // Apply sort
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [deliverables, sortField, sortDirection, nameFilter, criticalityFilter, phaseFilter, labelFilter]);

  // pagination calculations
  const totalPages = Math.ceil(filteredAndSortedDeliverables.length / localItemsPerPage);
  const startIndex = (currentPage - 1) * localItemsPerPage;
  const endIndex = startIndex + localItemsPerPage;
  const currentDeliverables = filteredAndSortedDeliverables.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [deliverables.length]);

  // progress color choosing
  const getProgressColorClass = (progress: number) => {
    if (progress === 100) {
      return styles.progressFull;
    } else if (progress >= 50) {
      return styles.progressMedium;
    } else {
      return styles.progressLow;
    }
  };
  

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setLocalItemsPerPage(newItemsPerPage);
    localStorage.setItem('deliverableTableItemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
    onItemsPerPageChange?.(newItemsPerPage);
  };
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [nameFilter, criticalityFilter, phaseFilter, labelFilter]);

  // Handle sorting when a column header is clicked
  const handleSort = (field: SortField) => {
    // same field click toggles direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Sort icon rendering
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <IconArrowNarrowDown className={styles.sortIconNeutral} />;
    }
    return sortDirection === 'asc' 
      ? <IconArrowNarrowDown className={styles.sortIconActive} /> 
      : <IconArrowNarrowDown className={`${styles.sortIconActive} ${styles.sortIconReversed}`} />;
  };

  return (
    <div className={styles.tableOuter}>
      <div className={styles.tableContainer}>
        <div className={styles.table}>
          <div className={styles.content}>
        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader} 
              onClick={() => handleSort('id')}
            >
              <span className={styles.headerText}>{t('table.loId')}</span>
              {renderSortIcon('id')}
            </div>
          </div>
          {currentDeliverables.map((deliverable, index) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                <span className={styles.text}>{deliverable.id}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
              onClick={() => handleSort('name')}
            >
              <span className={styles.headerText}>{t('table.name')}</span>
              {renderSortIcon('name')}
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                <span 
                  className={styles.truncatedText} 
                  title={deliverable.name}
                >
                  {deliverable.name.replaceAll('_', ' ').length > 25 
                    ? `${deliverable.name.replaceAll('_', ' ').substring(0, 25)}...` 
                    : deliverable.name.replaceAll('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>



        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
              onClick={() => handleSort('phase')}
            >
              <span className={styles.headerText}>{t('table.phase')}</span>
              {renderSortIcon('phase')}
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                <span className={styles.text}>{deliverable.phase}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
              onClick={() => handleSort('criticality')}
            >
              <span className={styles.headerText}>{t('table.criticality')}</span>
              {renderSortIcon('criticality')}
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                {deliverable.criticality ? (
                  <span 
                    className={`${styles.criticalityBadge} ${styles[deliverable.criticality.toLowerCase()]}`}
                  >
                    {deliverable.criticality === 'EASY' ? t('criticality.easy') : 
                     deliverable.criticality === 'MIDDLE' ? t('criticality.middle') : 
                     deliverable.criticality === 'HIGH' ? t('criticality.high') : deliverable.criticality}
                  </span>
                ) : (
                  <span className={`${styles.criticalityBadge} ${styles['not-assigned']}`}>
                    {t('table.notAssigned')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
              onClick={() => handleSort('date')}
            >
              <span className={styles.headerText}>{t('table.startDate')}</span>
              {renderSortIcon('date')}
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                <span className={styles.text}>{deliverable.startDate}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
            >
              <span className={styles.headerText}>{t('table.endDate')}</span>
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.textContent}>
                <span className={styles.text}>{deliverable.endDate}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.column}>
          <div className={styles.headerCell}>
            <div 
              className={styles.tableHeader}
              onClick={() => handleSort('progress')}
            >
              <span className={styles.headerText}>{t('table.progress')}</span>
              {renderSortIcon('progress')}
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.progressBar}>
                <div 
                  className={`${styles.progress} ${getProgressColorClass(deliverable.progress)}`} 
                  style={{ width: `${deliverable.progress}%` }}
                />
                <span className={styles.progressText}>{`${deliverable.progress}%`}</span>
              </div>
            </div>
          ))}
        </div>

            <div className={styles.column}>
                <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                        <span className={styles.headerText}>{t('table.labels')}</span>
                    </div>
                </div>
                {currentDeliverables.map((deliverable) => (
                    <div
                        key={deliverable.id}
                        className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
                        onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
                    >
                        <div className={styles.labelChips}>
                            {(deliverable.labels || []).map(label => (
                                <span
                                    key={label.id}
                                    className={styles.labelChip}
                                    style={{backgroundColor: label.color || '#e5e7eb'}}
                                    title={label.name}
                                >
                    {label.name}
                  </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

        <div className={`${styles.column} ${styles.lastColumn}`}>
          <div className={styles.headerCell}>
            <div className={styles.tableHeader}>
              <span className={styles.headerText}>{t('table.responsible')}</span>
            </div>
          </div>
          {currentDeliverables.map((deliverable) => (
            <div 
              key={deliverable.id} 
              className={`${styles.tableCell} ${onDeliverableClick ? styles.clickable : ''}`}
              onClick={() => onDeliverableClick && onDeliverableClick(deliverable)}
            >
              <div className={styles.assignees}>
                {deliverable.assignees.slice(0, 5).map((assignee, index) => (
                  <div 
                    key={assignee.id} 
                    className={styles.avatarWrapper}
                    style={{ zIndex: 5 - index }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img 
                      src={QIcon} 
                      alt={assignee.name || 'Responsible user'} 
                      className={styles.avatar} 
                      title={assignee.name || 'Responsible user'}
                    />
                  </div>
                ))}
                {deliverable.assignees.length > 5 && (
                  <div className={styles.moreAssignees}>
                    +{deliverable.assignees.length - 5}
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    </div>
    <div className={styles.pagination}>
        <div className={styles.paginationLeft}>
          <span className={styles.itemsPerPageLabel}>{t('table.pagination.show')}:</span>
          <select
            className={styles.itemsPerPageSelect}
            value={localItemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
          >
            <option value={5}>5</option>
            <option value={8}>8</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
          <span className={styles.itemsPerPageLabel}>{t('table.pagination.perPage')}</span>
        </div>

        <span className={styles.paginationDetails}>
          {totalPages > 0 ? (
            <>
              {t('table.pagination.page')} {currentPage} of {totalPages}
              {filteredAndSortedDeliverables.length > 0 && (
                <span> • {t('table.pagination.showing')} {startIndex + 1}-{Math.min(endIndex, filteredAndSortedDeliverables.length)} of {filteredAndSortedDeliverables.length}</span>
              )}
            </>
          ) : (
            t('table.noData')
          )}
        </span>

        <div className={styles.paginationRight}>
          <button
            className={styles.paginationButton}
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || totalPages === 0}
            aria-label="Go to previous page"
          >
            {t('table.pagination.previous')}
          </button>
          <button
            className={styles.paginationButton}
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            aria-label="Go to next page"
          >
            {t('table.pagination.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliverableTable;
